/**
 * Data layer for `behavioral-anomaly-detection-abnormal-user-actions`.
 * Thin typed fetch wrappers over /api/v1/behavioral-anomaly-detection-abnormal-user-actions.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export interface AnomalyEvent {
	id: string;
	event_id: string;
	actor_id: string;
	signal: string;
	severity: number;
	metadata: Record<string, unknown>;
	detected_at: number;
	created_at: number;
}

export interface ListAnomalyEventsResponse {
	items: AnomalyEvent[];
}

export interface CreateAnomalyEventBody {
	event_id: string;
	actor_id: string;
	signal: string;
	severity: number;
	metadata?: Record<string, unknown>;
	detected_at: number;
}

export interface ListFilters {
	actor_id?: string;
	signal?: string;
	limit?: number;
}

export const KNOWN_SIGNALS = [
	"mimicked_device",
	"abnormal_click_cadence",
	"impossible_travel",
	"replayed_session",
	"device_spoofing",
	"touch_entropy_low",
	"scripted_actions",
	"session_age_mismatch",
] as const;

const BASE = "/api/v1/behavioral-anomaly-detection-abnormal-user-actions";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	filters: ListFilters = {},
): Promise<ListAnomalyEventsResponse> {
	const qs = new URLSearchParams();
	if (filters.actor_id) qs.set("actor_id", filters.actor_id);
	if (filters.signal) qs.set("signal", filters.signal);
	if (filters.limit !== undefined) qs.set("limit", String(filters.limit));
	const suffix = qs.toString() ? `?${qs.toString()}` : "";
	const res = await fetch(`${BASE}${suffix}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAnomalyEventsResponse>(res);
}

export async function create(
	body: CreateAnomalyEventBody,
): Promise<{ ok: true }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: true }>(res);
}

export const behavioralAnomalyQueryKeys = {
	all: ["behavioral-anomaly-detection-abnormal-user-actions"] as const,
	list: (filters: ListFilters = {}) =>
		[
			...behavioralAnomalyQueryKeys.all,
			"list",
			filters.actor_id ?? "",
			filters.signal ?? "",
			filters.limit ?? "",
		] as const,
};
